/**
 * Railing Tool - Railing Creation by Sketch
 * 
 * Railings are created by sketching a path.
 * Features:
 * - Path sketch mode
 * - Host selection (Stair/Ramp/Floor) - Future
 * - Railing types
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface RailingGeometryData {
    path: [number, number, number][]; // 3D path points
    height: number;
    offset: number;
    levelId: string;
}

export interface RailingElement extends Element {
    category: 'Railings';
    geometryData: RailingGeometryData;
}

export class RailingTool extends BaseTool {
    id = 'railing';
    name = 'Railing';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 2) {
                this.finishSketch();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'polyline', // Using polyline for railing path
                points: previewPoints,
                color: '#ff00ff'
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishSketch() {
        if (!this.context) return;

        const path: [number, number, number][] = this.points.map(p => [p.x, p.y, p.z]);

        const geometryData: RailingGeometryData = {
            path: path,
            height: 1.1, // 1100mm default
            offset: 0,
            levelId: 'Level 1',
            // hostId: selectedHostId // Future: Add host picking
        };

        const railing: RailingElement = {
            id: uuidv4(),
            category: 'Railings',
            type: 'Railing: 1100mm',
            parameters: {
                'Base Level': 'Level 1',
                'Height': '1.10 m',
                'Length': calculatePathLength(path).toFixed(2) + ' m'
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(railing);

        this.points = [];
        this.context.appStore.setToolPreview(null);
    }
}

function calculatePathLength(points: [number, number, number][]): number {
    let length = 0;
    for (let i = 0; i < points.length - 1; i++) {
        const p1 = new THREE.Vector3(...points[i]);
        const p2 = new THREE.Vector3(...points[i + 1]);
        length += p1.distanceTo(p2);
    }
    return length;
}
